import { useState } from "react";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { useToast } from "../lib/toast";
import { SkeletonList } from "../components/Skeleton";
import type { AdminUser } from "../api/types";

function AssignTeam({
  user,
  onChanged,
}: {
  user: AdminUser;
  onChanged: () => void;
}) {
  const teams = useAsync(() => api.listTeams(true), []);
  const [teamId, setTeamId] = useState("");
  const [role, setRole] = useState<"admin" | "parent">("parent");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const assign = async () => {
    const tid = teamId || teams.data?.items[0]?.teamId;
    if (!tid) return;
    setBusy(true);
    try {
      await api.adminAssignTeam(user.userId, tid, role);
      toast.success(`Assigned ${user.email ?? user.userId} to team`);
      onChanged();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="row" style={{ gap: "0.4rem", marginTop: "0.5rem" }}>
      <select
        style={{ width: "auto" }}
        value={teamId}
        onChange={(e) => setTeamId(e.target.value)}
      >
        <option value="">Select team…</option>
        {teams.data?.items.map((t) => (
          <option key={t.teamId} value={t.teamId}>
            {t.name ?? t.teamId}
          </option>
        ))}
      </select>
      <select
        style={{ width: "auto" }}
        value={role}
        onChange={(e) => setRole(e.target.value as "admin" | "parent")}
      >
        <option value="parent">parent</option>
        <option value="admin">admin</option>
      </select>
      <button className="secondary" onClick={assign} disabled={busy}>
        {busy ? "Assigning…" : "Assign"}
      </button>
    </div>
  );
}

export function AdminUsers() {
  const { data, loading, error, reload } = useAsync(() => api.adminListUsers(), []);
  const [q, setQ] = useState("");
  const toast = useToast();

  const remove = async (userId: string, teamId: string) => {
    try {
      await api.adminRemoveTeam(userId, teamId);
      toast.success("Removed from team");
      reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const users = (data?.items ?? []).filter((u) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return [u.email, u.givenName, u.familyName, u.username]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(needle);
  });

  return (
    <div>
      <h1>Users</h1>
      <p className="muted">
        All registered users. Assign or remove team memberships and set each
        member's role. {data ? `${data.count} user(s).` : ""}
      </p>

      <div className="card">
        <input
          placeholder="Search name / email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {loading && <SkeletonList count={4} variant="card" />}
      {error && <p className="error">{error}</p>}

      <div className="stagger">
        {users.map((u) => (
          <div key={u.userId} className="card">
            <div className="row spread">
              <div>
                <strong>
                  {u.givenName || u.familyName
                    ? `${u.givenName ?? ""} ${u.familyName ?? ""}`.trim()
                    : u.email ?? u.userId}
                </strong>
                <div className="muted" style={{ fontSize: "0.85rem" }}>
                  {u.email}
                  {u.email ? " · " : ""}
                  {u.provider}
                  {u.enabled === false ? " · disabled" : ""}
                </div>
              </div>
              <span className="badge state">{u.status ?? ""}</span>
            </div>

            <div style={{ marginTop: "0.6rem" }}>
              <span className="muted" style={{ fontSize: "0.85rem" }}>
                Teams:
              </span>{" "}
              {u.teams.length === 0 && (
                <span className="muted">none</span>
              )}
              <span className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
                {u.teams.map((t) => (
                  <span key={t.teamId} className="badge accent">
                    {t.name} · {t.role}{" "}
                    <button
                      className="toast-close"
                      title="Remove from team"
                      style={{ padding: 0, marginLeft: 4 }}
                      onClick={() => remove(u.userId, t.teamId)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </span>
            </div>

            <AssignTeam user={u} onChanged={reload} />
          </div>
        ))}
      </div>

      {data && users.length === 0 && (
        <p className="muted">No users match your search.</p>
      )}
    </div>
  );
}
